import { useCallback, useEffect, useState } from 'react';
import {
  DropdownChangeEvent,
  DropdownProps,
  Dropdown as OriginalDropdown,
} from '@agro1desenvolvimento/react-components';
import { classNames } from 'primereact/utils';
import WithController from '../with-controller';
import InputEnhancements, { FormInputEnhancements } from './input-enhancements';

const MIN_LENGTH_TO_SHOW_SEARCH = 5;

const Dropdown = WithController<PropTypes, any>(
  ({
    field,
    fieldState,
    addonRight,
    addonLeft,
    label,
    withErrorMessage,
    containerClassName,
    formState: _formState,
    className,
    onChange,
    triggerDependencies,
    visible = true,
    onFilter,
    loading,
    emptyMessage,
    pt: externalPt,
    ...props
  }) => {
    const [filterText, setFilterText] = useState('');

    useEffect(() => {
      if (field.value == null) setFilterText('');
    }, [field.value]);

    const handleFilter = useCallback(
      (text: string) => {
        setFilterText(text);
        onFilter?.({ filter: text } as any);
      },
      [onFilter],
    );

    const pt = onFilter
      ? {
          ...externalPt,
          filterInput: {
            ...externalPt?.filterInput,
            value: filterText,
            onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
              handleFilter(e.target.value),
          },
        }
      : externalPt;

    const onChangeValue = (e: DropdownChangeEvent) => {
      field.onChange(e.value);

      if (onChange) onChange(e);

      triggerDependencies();
    };

    return (
      <InputEnhancements
        addonRight={addonRight}
        addonLeft={addonLeft}
        label={label}
        withErrorMessage={withErrorMessage}
        containerClassName={containerClassName}
        field={field}
        fieldState={fieldState}
        visible={visible}
      >
        <OriginalDropdown
          filter={
            onFilter
              ? true
              : (props.options?.length || 0) > MIN_LENGTH_TO_SHOW_SEARCH
          }
          {...props}
          emptyMessage={loading ? 'Carregando...' : emptyMessage}
          pt={pt}
          inputId={field.name}
          name={field.name}
          focusInputRef={field.ref}
          value={field.value}
          onBlur={field.onBlur}
          onChange={onChangeValue}
          className={classNames({ 'p-invalid': fieldState.error }, className)}
        />
      </InputEnhancements>
    );
  },
);

type OmittedProps =
  | 'inputId'
  | 'name'
  | 'inputRef'
  | 'value'
  | 'onBlur'
  | 'onValueChange'
  | 'emptyMessage';

type PropTypes = Omit<DropdownProps, OmittedProps> &
  FormInputEnhancements & {
    loading?: boolean;
    emptyMessage?: string;
  };

export default Dropdown;
